import { NavLink } from "react-router";

import { cx } from "@/lib/cx";

/**
 * Story 15.17 — the one shared sub-nav for the three real screens living
 * under the single "Tenants & Leases" nav destination (`destinations.ts`'s
 * own `tenants-leases` entry): Tenants (`/tenants-leases`), Leases
 * (`/tenants-leases/leases`) and Renewal board (`/tenants-leases/renewals`).
 *
 * Before this story, `TenantsList.tsx` and `LeasesList.tsx` each carried a
 * one-off "Renewal board" link and `RenewalBoard.tsx` linked to neither —
 * nothing linked the two lists to each other at all, which made the Leases
 * list genuinely unreachable from the UI (Owner-caught 2026-08-26, this
 * story's own Intent). This component replaces every one-off link with one
 * real, symmetric tab row rendered identically on all three pages.
 *
 * Real `NavLink`s, not a JS-only tab switch like `ReferenceLists.tsx`'s own
 * `role="tab"` buttons — those swap local state on a single route; these
 * three tabs are three different routes, so a real click, browser back/
 * forward, and a direct URL/bookmark all have to work exactly as they would
 * from any other link in the app (VC-15.17-03). `NavLink`'s own URL-match is
 * what marks the current page's tab active — mirrors `NavItem.tsx`'s
 * identical reasoning, not local component state — and it applies
 * `aria-current="page"` to the active link automatically, the same signal
 * the primary nav rail already relies on (VC-15.17-02).
 *
 * `end` on every tab: without it, `/tenants-leases` would prefix-match
 * `/tenants-leases/leases` and `/tenants-leases/renewals` too, marking the
 * Tenants tab active everywhere.
 */
const TABS: readonly { label: string; to: string }[] = [
  { label: "Tenants", to: "/tenants-leases" },
  { label: "Leases", to: "/tenants-leases/leases" },
  { label: "Renewal board", to: "/tenants-leases/renewals" },
];

export function TenantsLeasesSubNav() {
  return (
    <nav aria-label="Tenants & Leases" className="mb-4">
      <ul className="flex flex-wrap gap-2">
        {TABS.map((tab) => (
          <li key={tab.to}>
            <NavLink
              to={tab.to}
              end
              className={({ isActive }) =>
                cx(
                  "inline-flex h-tap-target-min items-center rounded-md border px-4 text-button",
                  isActive
                    ? "border-accent bg-accent-quiet text-accent"
                    : "border-border-strong bg-canvas text-ink hover:bg-surface-hover",
                )
              }
            >
              {tab.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
